import React, { useState, useEffect, useRef } from 'react';
import { IdsButton } from '@investec/ids-web-react';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import modulusCheck from '../../utils/modulusCheck';
import IsaTransferFaqModals from './IsaTransferFaqModals';
import { normaliseBacsRollNumber, validateBacsRollNumber } from './rollNumberValidation';

import './isaTransferProviderDetails.scss';

interface IsaTransferProvider {
    id: string;
    name: string;
    serviceUserNumber: string;
}

interface IsaTransferProviderDetailsStepProps {
    initialData?: IsaTransferProviderDetailsData | null;
    onBack: () => void;
    onContinue: (data: IsaTransferProviderDetailsData) => void;
}

interface IsaTransferProviderDetailsData {
    provider: IsaTransferProvider;
    sortCode?: string;
    accountNumber: string;
    rollNumber?: string;
}

export type { IsaTransferProviderDetailsData, IsaTransferProvider };

const IsaTransferProviderDetailsStep: React.FC<IsaTransferProviderDetailsStepProps> = ({
    initialData,
    onBack,
    onContinue
}) => {
    const analytics = useUnifiedAnalytics();
    const [providers, setProviders] = useState<IsaTransferProvider[]>([]);
    const [selectedProvider, setSelectedProvider] = useState<IsaTransferProvider | null>(initialData?.provider ?? null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [sortCode, setSortCode] = useState(initialData?.sortCode ?? '');
    const [accountNumber, setAccountNumber] = useState(initialData?.accountNumber ?? '');
    const [rollNumber, setRollNumber] = useState(initialData?.rollNumber ?? '');
    const [rollNumberTouched, setRollNumberTouched] = useState(false);
    const [rollNumberError, setRollNumberError] = useState<string | null>(null);
    const [showProviderNotListedModal, setShowProviderNotListedModal] = useState(false);
    const [showISATransferModal, setShowISATransferModal] = useState(false);
    const [isCheckingAccount, setIsCheckingAccount] = useState(false);
    const [accountValidationError, setAccountValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'provider details view',
                {},
                'ISA Transfer Provider Details',
                'ISA_Transfer_Provider_Details|Page_Load|Details_View'
            );
        }
    }, [analytics]);

    useEffect(() => {
        const fetchProviders = async () => {
            try {
                const response = await fetch('/api/isa-transfer-providers');
                if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
                const data = await response.json();
                setProviders(data);
            } catch (error) {
                console.error('Error fetching ISA transfer providers:', error);
                setProviders([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProviders();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleContinue = async () => {
        if (!selectedProvider || !canContinue) return;

        if (hasAccountDetails) {
            setIsCheckingAccount(true);
            setAccountValidationError(null);

            try {
                const isAccountValid = await modulusCheck(
                    getRawDigits(accountNumber),
                    getRawDigits(sortCode)
                );

                if (!isAccountValid) {
                    setAccountValidationError('Looks like this sort code and account number is not recognised');
                    return;
                }
            } catch {
                setAccountValidationError('We could not check these account details. Please try again.');
                return;
            } finally {
                setIsCheckingAccount(false);
            }
        }

        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'continue',
                '',
                'isa transfer provider details',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Provider_Details|Button_Click|Continue',
                'ISA Transfer Provider Details'
            );
        }
        onContinue({
            provider: selectedProvider,
            sortCode: sortCode.trim() || undefined,
            accountNumber: accountNumber.trim(),
            rollNumber: rollNumber.trim() || undefined,
        });
    };

    const handleChooseDifferent = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'choose different provider',
                '',
                'isa transfer provider details',
                'ISA Transfer',
                selectedProvider?.id ?? '',
                {},
                'click',
                'ISA_Transfer_Provider_Details|Link_Click|Choose_Different_Provider',
                'ISA Transfer Provider Details'
            );
        }
        setSelectedProvider(null);
    };

    const formatSortCode = (value: string): string => {
        const digits = value.replace(/\D/g, '').slice(0, 6);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
        return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
    };

    const formatAccountNumber = (value: string): string => {
        return value.replace(/\D/g, '').slice(0, 8);
    };

    const handleRollNumberChange = (value: string): void => {
        const normalisedValue = normaliseBacsRollNumber(value);
        setRollNumber(normalisedValue);

        if (rollNumberTouched) {
            setRollNumberError(validateBacsRollNumber(normalisedValue));
        }
    };

    const handleRollNumberBlur = (): void => {
        setRollNumberTouched(true);
        setRollNumberError(validateBacsRollNumber(rollNumber));
    };

    // Validation: enable Continue when user provides enough detail
    // The ISA can be identified by sort code + account number, OR roll number
    const getRawDigits = (value: string): string => value.replace(/\D/g, '');
    const hasValidAccountNumber = getRawDigits(accountNumber).length === 8;
    const currentRollNumberError = validateBacsRollNumber(rollNumber);
    const hasValidRollNumber = rollNumber.trim().length > 0 && currentRollNumberError === null;
    const hasValidSortCode = getRawDigits(sortCode).length === 6;
    const hasAccountDetails = hasValidAccountNumber && hasValidSortCode;
    const hasStartedAccountDetails = Boolean(accountNumber.trim() || sortCode.trim());
    const canContinue = currentRollNumberError === null &&
        (hasStartedAccountDetails ? hasAccountDetails : hasValidRollNumber);

    const RelatedFaqLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
        <button className="isa-transfer-provider-details__faq-link" type="button" onClick={onClick}>
            <span>{label}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="#286B6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </button>
    );

    if (isLoading) {
        return (
            <main className="isa-transfer-provider-details">
                <p>Loading providers...</p>
            </main>
        );
    }

    // Phase 1: Provider selection (dropdown)
    if (!selectedProvider) {
        return (
            <main className="isa-transfer-provider-details">
                <h1>Who is your current Cash ISA provider?</h1>

                <div className="isa-transfer-provider-details__content">
                    <div className="isa-transfer-provider-details__intro">
                        <h2>Find your provider</h2>
                        <p>We can only accept Cash ISA transfers from the providers listed below. If yours isn&apos;t listed, you won&apos;t be able to transfer your cash ISA to us. However, you can still add money to your ISA from your linked account or Online Flexi Saver if you have unused annual ISA allowance. If you have another Investec Save Instant Access Cash ISA, you can also transfer money from that account.</p>
                    </div>

                    <div className="isa-transfer-provider-details__field-group">
                        <div className="isa-transfer-provider-details__field-dropdown" ref={dropdownRef}>
                            <div
                                className="isa-transfer-provider-details__dropdown-trigger"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                role="combobox"
                                aria-expanded={isDropdownOpen}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setIsDropdownOpen(!isDropdownOpen);
                                    }
                                }}
                            >
                                <span className={selectedProvider ? 'isa-transfer-provider-details__dropdown-selected' : 'isa-transfer-provider-details__dropdown-placeholder'}>
                                    {selectedProvider?.name ?? 'Select'}
                                </span>
                                <svg className="isa-transfer-provider-details__dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#30384A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            {isDropdownOpen && (
                                <div className="isa-transfer-provider-details__dropdown-menu">
                                    {providers.map(provider => (
                                        <button
                                            key={provider.id}
                                            className={`isa-transfer-provider-details__dropdown-item ${selectedProvider?.id === provider.id ? 'isa-transfer-provider-details__dropdown-item--selected' : ''}`}
                                            onClick={() => {
                                                setSelectedProvider(provider);
                                                setIsDropdownOpen(false);
                                            }}
                                        >
                                            {provider.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    // Phase 2: Provider details form (teal card + all fields)
    return (
        <main className="isa-transfer-provider-details">
            <h1>Who is your current Cash ISA provider?</h1>

            <div className="isa-transfer-provider-details__content">
                <div className="isa-transfer-provider-details__intro">
                    <h2>Find your provider</h2>
                    <p>We can only accept Cash ISA transfers from the providers listed below. If yours isn&apos;t listed, you won&apos;t be able to transfer your cash ISA to us. However, you can still add money to your ISA from your linked account or Online Flexi Saver if you have unused annual ISA allowance. If you have another Investec Save Instant Access Cash ISA, you can also transfer money from that account.</p>
                </div>

                {/* Provider results - this would show the ISA provider-results instance */}
                <div className="isa-transfer-provider-details__provider-results">
                    {/* Placeholder for provider results component */}
                </div>

                {/* Selected provider teal card */}
                <div className="isa-transfer-provider-details__selected-provider">
                    <div className="isa-transfer-provider-details__selected-provider-name">
                        {selectedProvider.name}
                    </div>
                    <button
                        className="isa-transfer-provider-details__choose-different"
                        type="button"
                        onClick={handleChooseDifferent}
                    >
                        Choose a different provider
                    </button>
                </div>

                {/* Transfer details form */}
                <div className="isa-transfer-provider-details__transfer-details">
                    <h3 className="isa-transfer-provider-details__section-heading">
                        What details do you have for the Cash ISA you&apos;re transferring
                    </h3>

                    <p className="isa-transfer-provider-details__section-description">
                        There are a few ways your provider may identify your ISA. You can enter:
                    </p>

                    <div className="isa-transfer-provider-details__info-list">
                        <div className="isa-transfer-provider-details__info-item">
                            <span className="isa-transfer-provider-details__info-bullet" />
                            <div className="isa-transfer-provider-details__info-content">
                                <strong className="isa-transfer-provider-details__info-label">Sort code and account number</strong>
                                <em className="isa-transfer-provider-details__info-subtitle">Most ISA providers will give you this combination.</em>
                            </div>
                        </div>
                        <div className="isa-transfer-provider-details__info-item">
                            <span className="isa-transfer-provider-details__info-bullet" />
                            <div className="isa-transfer-provider-details__info-content">
                                <strong className="isa-transfer-provider-details__info-label">Roll number or account reference</strong>
                                <em className="isa-transfer-provider-details__info-subtitle">Building societies and investment platforms might give you this.</em>
                            </div>
                        </div>
                        <div className="isa-transfer-provider-details__info-item">
                            <span className="isa-transfer-provider-details__info-bullet" />
                            <div className="isa-transfer-provider-details__info-content">
                                <strong className="isa-transfer-provider-details__info-label">All of the above</strong>
                                <em className="isa-transfer-provider-details__info-subtitle">Some providers will give you all of these details.</em>
                            </div>
                        </div>
                    </div>

                    <p className="isa-transfer-provider-details__info-note">
                        If you&apos;re unsure, please check with your current provider.
                    </p>

                    <div className="isa-transfer-provider-details__fields">
                        <div className="isa-transfer-provider-details__field">
                            <label htmlFor="sortCode">Sort code</label>
                            <input
                                id="sortCode"
                                type="text"
                                className="isa-transfer-provider-details__input"
                                placeholder="XX-XX-XX"
                                value={sortCode}
                                onChange={(e) => setSortCode(formatSortCode(e.target.value))}
                                inputMode="numeric"
                                maxLength={8}
                                disabled={isCheckingAccount}
                            />
                        </div>
                        <div className="isa-transfer-provider-details__field">
                            <label htmlFor="accountNumber">Account number</label>
                            <input
                                id="accountNumber"
                                type="text"
                                className="isa-transfer-provider-details__input"
                                placeholder="12345678"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(formatAccountNumber(e.target.value))}
                                inputMode="numeric"
                                maxLength={8}
                                disabled={isCheckingAccount}
                            />
                        </div>
                        <div className="isa-transfer-provider-details__field">
                            <label htmlFor="rollNumber">Roll number or account reference (if applicable)</label>
                            <input
                                id="rollNumber"
                                type="text"
                                className="isa-transfer-provider-details__input"
                                placeholder="1234/123456789"
                                value={rollNumber}
                                onChange={(e) => handleRollNumberChange(e.target.value)}
                                onBlur={handleRollNumberBlur}
                                maxLength={18}
                                disabled={isCheckingAccount}
                            />
                            {rollNumberTouched && rollNumberError && (
                                <p className="isa-transfer-provider-details__validation-error" role="alert">
                                    {rollNumberError}
                                </p>
                            )}
                        </div>
                    </div>

                    {isCheckingAccount && (
                        <p className="isa-transfer-provider-details__checking-message" aria-live="polite">
                            Checking your account details...
                        </p>
                    )}

                    {accountValidationError && (
                        <p className="isa-transfer-provider-details__validation-error" role="alert">
                            {accountValidationError}
                        </p>
                    )}

                    <div className="isa-transfer-provider-details__actions">
                        <IdsButton appearance="default" size="md" onClick={() => {
                            if (analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'back',
                                    '',
                                    'isa transfer provider details',
                                    'ISA Transfer',
                                    '',
                                    {},
                                    'click',
                                    'ISA_Transfer_Provider_Details|Button_Click|Back',
                                    'ISA Transfer Provider Details'
                                );
                            }
                            onBack();
                        }}>
                            Back
                        </IdsButton>
                        <IdsButton
                            appearance="primary"
                            size="md"
                            disabled={!canContinue || isCheckingAccount}
                            onClick={handleContinue}
                        >
                            Continue
                        </IdsButton>
                    </div>
                </div>
            </div>

            <div className="isa-transfer-provider-details__divider" />

            <div className="isa-transfer-provider-details__faq-section">
                <h3>Related FAQs</h3>
                <div className="isa-transfer-provider-details__faq-links">
                    <RelatedFaqLink label="What if my provider isn't listed?" onClick={() => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'what if my provider isnt listed',
                '',
                'isa transfer provider details',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Provider_Details|Link_Click|Whats_If_Provider_Not_Listed',
                'ISA Transfer Provider Details'
            );
        }
        setShowProviderNotListedModal(true);
    }} />
                    <RelatedFaqLink label="How does the ISA transfer in work?" onClick={() => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'how does isa transfer work',
                '',
                'isa transfer provider details',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Provider_Details|Link_Click|How_Does_ISA_Transfer_Work',
                'ISA Transfer Provider Details'
            );
        }
        setShowISATransferModal(true);
    }} />
                </div>
            </div>

            <IsaTransferFaqModals
                providers={providers}
                showProviderNotListed={showProviderNotListedModal}
                onCloseProviderNotListed={() => setShowProviderNotListedModal(false)}
                showISATransfer={showISATransferModal}
                onCloseISATransfer={() => setShowISATransferModal(false)}
            />
        </main>
    );
};

export default IsaTransferProviderDetailsStep;
